/**
 * MathEditor Component - WYSIWYG math input using MathQuill
 *
 * Features:
 * - Auto-trigger: type `3/4` → fraction, `x^2` → exponent, `sqrt(16)` → √16
 * - Smart navigation: Arrow/Tab at field edge exits to text
 * - Empty field cleanup: Backspace on empty field removes it
 * - Math keyboard for mobile (🔢 button)
 * - Outputs LaTeX with $ delimiters for storage
 */
import { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import './MathEditor.css';

// jQuery and MathQuill are loaded via CDN in index.html
// Access them from window
declare global {
  interface Window {
    jQuery: any;
    MathQuill: any;
  }
}

interface MathEditorProps {
  /** Current LaTeX value (for controlled component) */
  value?: string;
  /** Called when content changes, with LaTeX output */
  onChange?: (latex: string) => void;
  /** Called when Enter is pressed (outside of math field editing) */
  onSubmit?: () => void;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Additional CSS class */
  className?: string;
  /** Show math keyboard toggle */
  showKeyboard?: boolean;
  /** Disable editing */
  disabled?: boolean;
}

interface MathFieldData {
  wrapper: HTMLSpanElement;
  field: any; // MathQuill MathField instance
}

/** Ref type for MathEditor - exposes focus method */
export interface MathEditorRef {
  focus: () => void;
}

export const MathEditor = forwardRef<MathEditorRef, MathEditorProps>(function MathEditor({
  value,
  onChange,
  onSubmit,
  placeholder = "Type your answer...",
  className = '',
  showKeyboard = true,
  disabled = false
}, ref) {
  const editorRef = useRef<HTMLDivElement>(null);

  // Expose focus method via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      editorRef.current?.focus();
    }
  }), []);
  const mathFieldsRef = useRef<MathFieldData[]>([]);
  const mqRef = useRef<any>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  // Clear editor when value prop becomes empty (for controlled clearing)
  useEffect(() => {
    if (value === '' && editorRef.current) {
      // Clear all math fields
      mathFieldsRef.current.forEach(f => f.wrapper.remove());
      mathFieldsRef.current = [];
      // Clear text content
      editorRef.current.textContent = '';
    }
  }, [value]);

  // Get LaTeX output from editor content
  const getLatexOutput = useCallback(() => {
    if (!editorRef.current) return '';

    const parts: string[] = [];

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.replace(/\u00A0/g, ' ') || '';
        if (text) parts.push(text);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.classList.contains('math-field-wrapper')) {
          const fieldData = mathFieldsRef.current.find(f => f.wrapper === el);
          if (fieldData) {
            const latex = fieldData.field.latex();
            if (latex) parts.push('$' + latex + '$');
          }
        } else if (el.tagName === 'BR') {
          parts.push('\n');
        } else {
          el.childNodes.forEach(processNode);
        }
      }
    };

    editorRef.current.childNodes.forEach(processNode);
    return parts.join('').trim();
  }, []);

  // Update output and notify parent
  const updateOutput = useCallback(() => {
    const latex = getLatexOutput();
    onChange?.(latex);
  }, [getLatexOutput, onChange]);

  // Exit math field to the right
  const exitMathField = useCallback((wrapper: HTMLElement) => {
    const range = document.createRange();
    range.setStartAfter(wrapper);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    editorRef.current?.focus();
  }, []);

  // Exit math field to the left
  const exitMathFieldLeft = useCallback((wrapper: HTMLElement) => {
    const range = document.createRange();
    range.setStartBefore(wrapper);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    editorRef.current?.focus();
  }, []);

  // Insert a MathQuill field at cursor
  const insertMathField = useCallback((initialLatex: string = '') => {
    if (!editorRef.current || !mqRef.current) return null;

    const sel = window.getSelection();
    let range: Range;

    // Ensure we're in the editor
    if (!sel || !editorRef.current.contains(sel.anchorNode) && editorRef.current !== sel.anchorNode) {
      editorRef.current.focus();
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
    } else {
      range = sel.getRangeAt(0);
    }

    // Create wrapper span
    const wrapper = document.createElement('span');
    wrapper.className = 'math-field-wrapper';
    wrapper.contentEditable = 'false';

    // Create MathQuill span
    const mqSpan = document.createElement('span');
    mqSpan.className = 'mq-field';
    wrapper.appendChild(mqSpan);

    // Insert into editor
    range.deleteContents();
    range.insertNode(wrapper);

    // Add space after if needed
    const space = document.createTextNode('\u00A0');
    wrapper.after(space);

    const MQ = mqRef.current;

    // Initialize MathQuill with handlers
    const mathField = MQ.MathField(mqSpan, {
      spaceBehavesLikeTab: true,
      handlers: {
        edit: () => updateOutput(),
        enter: () => exitMathField(wrapper),
        moveOutOf: (direction: number) => {
          if (direction === MQ.R) exitMathField(wrapper);
          else if (direction === MQ.L) exitMathFieldLeft(wrapper);
        },
        deleteOutOf: (_direction: number, mf: any) => {
          if (mf.latex() === '') {
            mathFieldsRef.current = mathFieldsRef.current.filter(f => f.wrapper !== wrapper);
            wrapper.remove();
            editorRef.current?.focus();
            updateOutput();
          }
        }
      }
    });

    // Store reference
    mathFieldsRef.current.push({ wrapper, field: mathField });

    // Set initial LaTeX if provided
    if (initialLatex) {
      mathField.latex(initialLatex);
    }

    // Focus the math field
    mathField.focus();
    updateOutput();

    return mathField;
  }, [updateOutput, exitMathField, exitMathFieldLeft]);

  // Get currently focused MathQuill field
  const getActiveMathField = useCallback(() => {
    const activeWrapper = document.activeElement?.closest('.math-field-wrapper');
    if (activeWrapper) {
      const fieldData = mathFieldsRef.current.find(f => f.wrapper === activeWrapper);
      return fieldData?.field || null;
    }
    return null;
  }, []);

  // Auto-trigger patterns
  const checkAutoTrigger = useCallback(() => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return false;

    const range = sel.getRangeAt(0);
    if (!range.collapsed) return false;

    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return false;

    const text = node.textContent?.substring(0, range.startOffset) || '';

    const patterns = [
      // Fraction: matches decimals like 12.5/2, but NOT in expressions
      { regex: /(\d+\.?\d*)\/(\d+\.?\d*)$/, convert: (m: RegExpMatchArray) => `\\frac{${m[1]}}{${m[2]}}`, fractionCheck: true },
      { regex: /([a-zA-Z0-9]+)\^(\d+|[a-zA-Z])$/, convert: (m: RegExpMatchArray) => `${m[1]}^{${m[2]}}` },
      { regex: /sqrt\(([^)]+)\)$/, convert: (m: RegExpMatchArray) => `\\sqrt{${m[1]}}` }
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern.regex);
      if (match) {
        const matchStart = match.index!;

        // Special check for fractions: don't convert if it's part of a variable expression
        if ((pattern as any).fractionCheck) {
          // Check character immediately before the match (e.g., the "x" in "12x5/2")
          if (matchStart > 0) {
            const charBefore = text[matchStart - 1];
            // If directly preceded by a letter (no space), it's a variable expression like "12x5/2"
            // But "12 x 5/2" (with spaces) is multiplication and 5/2 SHOULD become a fraction
            if (/[a-zA-Z]/.test(charBefore)) {
              continue;
            }
          }
        }

        const latex = pattern.convert(match);
        const matchEnd = range.startOffset;

        // Delete the matched text
        const deleteRange = document.createRange();
        deleteRange.setStart(node, matchStart);
        deleteRange.setEnd(node, matchEnd);
        deleteRange.deleteContents();

        // Insert math field with converted LaTeX
        insertMathField(latex);
        updateOutput();
        return true;
      }
    }
    return false;
  }, [insertMathField, updateOutput]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const wrapper = (document.activeElement as HTMLElement)?.closest('.math-field-wrapper');

    // Enter outside math field: trigger submit
    if (e.key === 'Enter' && !wrapper && onSubmit) {
      e.preventDefault();
      onSubmit();
      return;
    }

    // Tab inside math field: exit to right
    if (e.key === 'Tab' && wrapper) {
      e.preventDefault();
      exitMathField(wrapper as HTMLElement);
      return;
    }

    // Arrow keys from text: enter adjacent math fields
    if (wrapper) return; // Already in math field

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;

      const node = range.startContainer;
      const offset = range.startOffset;

      if (e.key === 'ArrowLeft') {
        let prevNode: Node | null = null;
        if (node.nodeType === Node.TEXT_NODE && offset === 0) {
          prevNode = node.previousSibling;
        } else if (node.nodeType === Node.ELEMENT_NODE && offset > 0) {
          prevNode = node.childNodes[offset - 1];
        }
        while (prevNode && prevNode.nodeType === Node.TEXT_NODE && prevNode.textContent?.trim() === '') {
          prevNode = prevNode.previousSibling;
        }
        if (prevNode && (prevNode as HTMLElement).classList?.contains('math-field-wrapper')) {
          e.preventDefault();
          const fieldData = mathFieldsRef.current.find(f => f.wrapper === prevNode);
          if (fieldData) {
            fieldData.field.focus();
            fieldData.field.moveToRightEnd();
          }
        }
      }

      if (e.key === 'ArrowRight') {
        let nextNode: Node | null = null;
        if (node.nodeType === Node.TEXT_NODE && offset === (node as Text).length) {
          nextNode = node.nextSibling;
        } else if (node.nodeType === Node.ELEMENT_NODE && offset < node.childNodes.length) {
          nextNode = node.childNodes[offset];
        }
        while (nextNode && nextNode.nodeType === Node.TEXT_NODE && nextNode.textContent?.trim() === '') {
          nextNode = nextNode.nextSibling;
        }
        if (nextNode && (nextNode as HTMLElement).classList?.contains('math-field-wrapper')) {
          e.preventDefault();
          const fieldData = mathFieldsRef.current.find(f => f.wrapper === nextNode);
          if (fieldData) {
            fieldData.field.focus();
            fieldData.field.moveToLeftEnd();
          }
        }
      }
    }
  }, [exitMathField, onSubmit]);

  // Handle input (for auto-trigger)
  const handleInput = useCallback(() => {
    setTimeout(() => {
      if (!checkAutoTrigger()) {
        updateOutput();
      }
    }, 0);
  }, [checkAutoTrigger, updateOutput]);

  // Track last focused element for restoring focus after keyboard button click
  const lastFocusedRef = useRef<{ type: 'editor' | 'mathfield', element?: any } | null>(null);

  // Save focus state when editor or math field is focused
  const saveFocusState = useCallback(() => {
    const mf = getActiveMathField();
    if (mf) {
      lastFocusedRef.current = { type: 'mathfield', element: mf };
    } else if (editorRef.current?.contains(document.activeElement)) {
      lastFocusedRef.current = { type: 'editor' };
    }
  }, [getActiveMathField]);

  // Handle keyboard button click
  const handleKeyboardBtn = useCallback((action: string, value?: string) => {
    let mf = getActiveMathField();

    // If no active math field, check if we had one recently or create new
    if (!mf && lastFocusedRef.current?.type === 'mathfield') {
      mf = lastFocusedRef.current.element;
    }

    // Navigation actions
    if (action === 'nav') {
      if (!mf) {
        // Focus the editor if no math field
        editorRef.current?.focus();
        return;
      }
      if (value === 'left') mf.keystroke('Left');
      else if (value === 'right') mf.keystroke('Right');
      else if (value === 'backspace') mf.keystroke('Backspace');
      else if (value === 'clear') mf.latex('');
      mf.focus();
      return;
    }

    // Insert actions - create field if needed
    if (!mf) {
      mf = insertMathField('');
    }
    if (!mf) return;

    if (action === 'cmd') mf.cmd(value);
    else if (action === 'latex') mf.cmd(value);
    else if (action === 'write') mf.write(value);

    mf.focus();
    updateOutput();
  }, [getActiveMathField, insertMathField, updateOutput]);

  // Prevent mousedown from stealing focus
  const preventFocusLoss = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // Initialize MathQuill
  useEffect(() => {
    if (window.MathQuill) {
      mqRef.current = window.MathQuill.getInterface(2);
    }
  }, []);

  return (
    <div className={`math-editor ${className}`}>
      <div className="math-editor-wrapper">
        <div
          ref={editorRef}
          className="math-editor-content"
          contentEditable={!disabled}
          data-placeholder={placeholder}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={saveFocusState}
          onClick={saveFocusState}
          suppressContentEditableWarning
        />
      </div>

      {/* Keyboard toggle - arrow below editor */}
      {showKeyboard && (
        <button
          type="button"
          className="math-keyboard-toggle"
          onClick={() => setKeyboardOpen(!keyboardOpen)}
          disabled={disabled}
          title="Math keyboard"
        >
          <span className={`toggle-arrow ${keyboardOpen ? 'open' : ''}`}>▼</span>
        </button>
      )}

      {/* Math Keyboard */}
      {keyboardOpen && (
        <div className="math-keyboard" onMouseDown={preventFocusLoss}>
          {/* Row 1: Controls */}
          <div className="math-keyboard-grid five-col">
            <button type="button" className="math-key primary" onClick={() => insertMathField('')} title="Insert equation">∑+</button>
            <button type="button" className="math-key danger" onClick={() => handleKeyboardBtn('nav', 'clear')} title="Clear">C</button>
            <button type="button" className="math-key nav" onClick={() => handleKeyboardBtn('nav', 'backspace')} title="Backspace">⌫</button>
            <button type="button" className="math-key nav" onClick={() => handleKeyboardBtn('nav', 'left')} title="Left">←</button>
            <button type="button" className="math-key nav" onClick={() => handleKeyboardBtn('nav', 'right')} title="Right">→</button>
          </div>
          {/* Row 2: Math structures */}
          <div className="math-keyboard-grid five-col">
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('cmd', '/')} title="Fraction">a/b</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('cmd', '^')} title="Exponent">x<sup>n</sup></button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '^2')} title="Square">x²</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\sqrt')} title="Square root">√</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\nthroot')} title="Nth root"><sup>n</sup>√</button>
          </div>
          {/* Row 3: Operators */}
          <div className="math-keyboard-grid five-col">
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '+')} title="Plus">+</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '-')} title="Minus">−</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\times')} title="Times">×</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\div')} title="Divide">÷</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '=')} title="Equals">=</button>
          </div>
          {/* Row 4: Comparisons */}
          <div className="math-keyboard-grid five-col">
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\ne')} title="Not equal">≠</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\le')} title="Less or equal">≤</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '<')} title="Less than">&lt;</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '>')} title="Greater than">&gt;</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\ge')} title="Greater or equal">≥</button>
          </div>
          {/* Row 5: Misc */}
          <div className="math-keyboard-grid five-col">
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '~')} title="Approximately">~</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\pm')} title="Plus/minus">±</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('latex', '\\pi')} title="Pi">π</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '(')} title="Left paren">(</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', ')')} title="Right paren">)</button>
          </div>
          {/* Row 6: Numbers 1-5 */}
          <div className="math-keyboard-grid five-col">
            {['1','2','3','4','5'].map(n => (
              <button key={n} type="button" className="math-key num" onClick={() => handleKeyboardBtn('write', n)}>{n}</button>
            ))}
          </div>
          {/* Row 7: Numbers 6-0 */}
          <div className="math-keyboard-grid five-col">
            {['6','7','8','9','0'].map(n => (
              <button key={n} type="button" className="math-key num" onClick={() => handleKeyboardBtn('write', n)}>{n}</button>
            ))}
          </div>
          {/* Row 8: Brackets and decimal */}
          <div className="math-keyboard-grid five-col">
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '[')} title="Left bracket">[</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', ']')} title="Right bracket">]</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '{')} title="Left brace">{'{'}</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '}')} title="Right brace">{'}'}</button>
            <button type="button" className="math-key" onClick={() => handleKeyboardBtn('write', '.')} title="Decimal">.</button>
          </div>
        </div>
      )}
    </div>
  );
});

export default MathEditor;
